import React, { useEffect, useState } from 'react';

import {
  CenterStack,
  Box,
  ButtonWithAction,
  withWindowSize,
  FormInput,
  Spinner,
} from '@thecb/components';
import WelcomeHeader from '../../components/welcome-header';
import { useOktaAuth } from '@okta/okta-react';
import { required, isProbablyEmail } from 'redux-freeform';
import {
  mapDispatchToProps,
  mapStateToProps,
  reducer,
} from './LoginForm.state';
import { submitLoginForm } from './Login.state';
import { useDispatch, useSelector } from 'react-redux';
import {
  consoleWarningStyle,
  LOGOUT_REASONS,
  OKTA_LOGOUT_REASON_KEY,
} from '../../utils/constants';
import { clearSharedAlerts, replaceSharedAlerts } from '../../state/shared';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';

const emailErrorMessages = {
  [required.error]: 'Email address is required',
  [isProbablyEmail.error]: 'Invalid email address',
};

export const LoginFormBody = ({
  actions,
  fields,
  showErrors,
  submitLoginForm,
  handleOktaLogin,
}) => {
  const dispatch = useDispatch();
  return (
    <Box as="form" id="login-form" key="item2" width="576px" padding="24px">
      <FormInput
        form="login-form"
        labelTextWhenNoError="Email Address"
        errorMessages={emailErrorMessages}
        type="email"
        name="email"
        field={fields.email}
        fieldActions={actions.fields.email}
        showErrors={showErrors}
        isEmail
        labelTextVariant="p"
        errorTextVariant="p"
      />
      <ButtonWithAction
        form="login-form"
        type="submit"
        disabled={false}
        text="Log In"
        extraStyles={'width: 100%; margin: 0px;'}
        action={(e) => {
          e.preventDefault();
          dispatch(submitLoginForm());
          handleOktaLogin();
        }}
        textExtraStyles="font-size: 1rem;"
      />
    </Box>
  );
};

// for redux-freeform validation mapping
LoginFormBody.reducer = reducer;
LoginFormBody.mapStateToProps = mapStateToProps;
LoginFormBody.mapDispatchToProps = mapDispatchToProps;

const Login = ({ actions, forms, isLoading = true }) => {
  const [isLoadingOkta, setIsLoadingOkta] = useState(isLoading);
  const [errors] = useState(false);
  const dispatch = useDispatch();
  const { oktaAuth, authState } = useOktaAuth();
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  const handleOktaLogin = async () => {
    try {
      let exists = await oktaAuth.session.exists();
      let isAuthenticated = await oktaAuth.isAuthenticated();
      if (exists && !isAuthenticated) {
        await oktaAuth.signInWithRedirect();
        localStorage.setItem(OKTA_LOGOUT_REASON_KEY, '');
      } else {
        setIsLoadingOkta(false);
      }
    } catch (err) {
      console.warn(
        `%cWarning: Okta sign in error: ${err}`,
        `background-color: ${consoleWarningStyle}`,
      );
      setIsLoadingOkta(false);
    }
  };

  useEffect(() => {
    handleOktaLogin();
  }, [authState]);

  useEffect(() => {
    const logoutReason = localStorage.getItem(OKTA_LOGOUT_REASON_KEY);
    let timeout;
    if (!!logoutReason) {
      dispatch(
        replaceSharedAlerts({
          heading: LOGOUT_REASONS[logoutReason]?.value ?? logoutReason,
          variant: LOGOUT_REASONS[logoutReason]?.variant ?? 'error',
        }),
      );
      timeout = setTimeout(() => {
        dispatch(clearSharedAlerts());
        localStorage.setItem(OKTA_LOGOUT_REASON_KEY, '');
      }, 10000);
    }
    return () => {
      dispatch(clearSharedAlerts());
      localStorage.setItem(OKTA_LOGOUT_REASON_KEY, '');
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  return isLoadingOkta ? (
    <CenterStack
      content={
        <Box padding="32px 0 64px">
          <Spinner size="100" />
        </Box>
      }
    />
  ) : (
    <CenterStack
      content={
        <Box padding="32px 0 64px">
          {sharedAlerts.length > 0 && (
            <Box padding="16px 0">
              <SharedAlertBar />
            </Box>
          )}
          <Box
            padding="0"
            borderRadius="8px"
            boxShadow="0px 1px 2px 0px rgba(41, 42, 51, 0.1), 0px 2px 6px 0px rgba(41, 42, 51, 0.2), 0px 1px 0px 0px rgba(41, 42, 51, 0.1) inset"
          >
            <WelcomeHeader actionText="Please log in to continue" />
            <LoginFormBody
              handleOktaLogin={handleOktaLogin}
              handleSubmit={handleOktaLogin}
              showErrors={errors}
              fields={forms.loginForm.fields}
              actions={actions.forms.loginForm.actions}
              submitLoginForm={submitLoginForm}
            />
          </Box>
        </Box>
      }
    />
  );
};

export default withWindowSize(Login);
